/** Visual / validation state for the HH:MM duration field. */
export type HoursFieldState = 'idle' | 'valid' | 'invalid';

export interface HhMmParseResult {
  readonly status: HoursFieldState;
  readonly hours: number | null;
  readonly message: string;
  readonly readable: string;
}

/** Strip non-digits and insert `:` after the first two digits (max HH:MM). */
export function formatHhMmDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/**
 * Expand shorthand duration on blur.
 * - `2` / `02` → `02:00` (whole hours)
 * - `230` → `02:30` (left-pad to HH:MM)
 * - `0215` → `02:15`
 */
export function expandHhMmShorthand(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (!digits.length) return '';
  if (digits.length <= 2) {
    const h = parseInt(digits, 10);
    if (!Number.isFinite(h)) return '';
    return `${String(h).padStart(2, '0')}:00`;
  }
  if (digits.length === 3) {
    return formatHhMmDigits(digits.padStart(4, '0'));
  }
  return formatHhMmDigits(digits);
}

/**
 * Whole minutes for display. Decimal `screen_hours` often include leftover
 * seconds (0.76 h = 45 m 36 s). Those seconds must not round the label up
 * to the next minute. A value already on a minute boundary stays there,
 * including binary float noise such as `(1 / 60) * 60`.
 */
export function wholeMinutesFromHours(hours: number): number {
  if (!Number.isFinite(hours) || hours <= 0) return 0;
  const minutes = hours * 60;
  const nearest = Math.round(minutes);
  if (Math.abs(minutes - nearest) < 1e-4) return nearest;
  return Math.floor(minutes);
}

/** Sum each duration's displayed minutes so a total cannot gain a minute the rows do not show. */
export function sumWholeMinutes(parts: readonly number[]): number {
  let total = 0;
  for (const hours of parts) total += wholeMinutesFromHours(hours);
  return total;
}

/** Decimal hours equal to {@link sumWholeMinutes}. Safe to pass back through the duration formatters. */
export function sumHoursTruncatedToMinutes(parts: readonly number[]): number {
  return sumWholeMinutes(parts) / 60;
}

/** Snap decimal hours to the nearest whole minute so 08:15 stays 8.25, not 8.3. */
export function roundHoursToNearestMinute(hours: number): number {
  if (!Number.isFinite(hours) || hours < 0) return 0;
  return Math.round(hours * 60) / 60;
}

/** Convert decimal hours (backend) → padded HH:MM for the input. */
export function decimalHoursToHhMm(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) return '';
  const totalMinutes = Math.min(24 * 60, wholeMinutesFromHours(hours));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Compact display: `48 min`, `1 hr`, `1 hr 20 min` (no seconds). */
export function formatCompactDuration(hours: number, minutes: number): string {
  const h = Math.max(0, Math.floor(hours));
  const m = Math.max(0, Math.floor(minutes));
  if (h <= 0 && m <= 0) return '0 min';
  if (h <= 0) return `${m} min`;
  if (m <= 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

/** Dense display for tight columns: `21 m`, `10 h`, `10 h 21 m`. */
export function formatDenseDuration(hours: number, minutes: number): string {
  const h = Math.max(0, Math.floor(hours));
  const m = Math.max(0, Math.floor(minutes));
  if (h <= 0 && m <= 0) return '0 m';
  if (h <= 0) return `${m} m`;
  if (m <= 0) return `${h} h`;
  return `${h} h ${m} m`;
}

/** Convert decimal hours → compact label (whole minutes; leftover seconds are dropped). */
export function formatCompactDurationFromHours(hours: number): string {
  const n = Number(hours);
  if (!Number.isFinite(n) || n <= 0) return '0 min';
  const totalMinutes = wholeMinutesFromHours(n);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return formatCompactDuration(h, m);
}

/** Convert decimal hours → dense `h` / `m` label (whole minutes; leftover seconds are dropped). */
export function formatDenseDurationFromHours(hours: number): string {
  const n = Number(hours);
  if (!Number.isFinite(n) || n <= 0) return '0 m';
  const totalMinutes = wholeMinutesFromHours(n);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return formatDenseDuration(h, m);
}

/** Human-readable HH:MM label, e.g. "1 hr 20 min". */
export function formatReadableDuration(hours: number, minutes: number): string {
  return formatCompactDuration(hours, minutes);
}

/**
 * Validate / parse HH:MM draft.
 * Valid complete values become decimal hours for the API (e.g. 02:12 → 2.2).
 */
export function parseHhMmInput(raw: string): HhMmParseResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { status: 'idle', hours: null, message: '', readable: '' };
  }

  const digits = trimmed.replace(/\D/g, '');
  if (!digits.length) {
    return { status: 'idle', hours: null, message: '', readable: '' };
  }

  // Incomplete while typing — no error yet unless hours already exceed 24.
  if (digits.length < 4) {
    const hourPart = parseInt(digits.slice(0, Math.min(2, digits.length)), 10);
    if (digits.length >= 2 && Number.isFinite(hourPart) && hourPart > 24) {
      return {
        status: 'invalid',
        hours: null,
        message: 'Hours cannot exceed 24.',
        readable: '',
      };
    }
    if (digits.length === 3) {
      const minuteTens = parseInt(digits[2]!, 10);
      if (Number.isFinite(minuteTens) && minuteTens > 5) {
        return {
          status: 'invalid',
          hours: null,
          message: 'Minutes cannot exceed 59.',
          readable: '',
        };
      }
    }
    return { status: 'idle', hours: null, message: '', readable: '' };
  }

  const hh = parseInt(digits.slice(0, 2), 10);
  const mm = parseInt(digits.slice(2, 4), 10);

  if (!Number.isFinite(hh) || !Number.isFinite(mm)) {
    return {
      status: 'invalid',
      hours: null,
      message: 'Enter duration as HH:MM.',
      readable: '',
    };
  }

  if (hh > 24) {
    return {
      status: 'invalid',
      hours: null,
      message: 'Hours cannot exceed 24.',
      readable: '',
    };
  }

  if (mm > 59) {
    return {
      status: 'invalid',
      hours: null,
      message: 'Minutes cannot exceed 59.',
      readable: '',
    };
  }

  if (hh === 24 && mm !== 0) {
    return {
      status: 'invalid',
      hours: null,
      message: 'When hours are 24, minutes must be 00.',
      readable: '',
    };
  }

  if (hh === 0 && mm === 0) {
    return {
      status: 'invalid',
      hours: null,
      message: 'Duration must be greater than 00:00.',
      readable: '',
    };
  }

  const decimalHours = hh + mm / 60;
  return {
    status: 'valid',
    hours: decimalHours,
    message: '',
    readable: formatReadableDuration(hh, mm),
  };
}

/** Block non-digit keys while allowing navigation / edit keys. */
export function isHhMmAllowedKey(event: KeyboardEvent): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return true;
  const allowed = new Set([
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ]);
  if (allowed.has(event.key)) return true;
  return /^\d$/.test(event.key);
}
